import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface MFAEnrollmentProps {
  onComplete: () => void;
  onSkip?: () => void;
  /** When set, the cancel control reads "Back to sign in" (sign-out semantics
      are the caller's onSkip) instead of offering a skippable enrollment. */
  required?: boolean;
}

export const MFAEnrollment: React.FC<MFAEnrollmentProps> = ({ onComplete, onSkip, required }) => {
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [step, setStep] = useState<'enroll' | 'verify'>('enroll');

  useEffect(() => {
    startEnrollment();
  }, []);

  const startEnrollment = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'RidePulse Admin TOTP',
      });
      if (enrollError) throw enrollError;
      setQrCode(data.totp.qr_code);
      setSecret(data.totp.secret);
      setFactorId(data.id);
      setStep('verify');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start enrollment');
    } finally {
      setLoading(false);
    }
  };

  const verifyFactor = async () => {
    if (!factorId || verifyCode.length !== 6) return;
    setLoading(true);
    setError(null);
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verifyCode,
      });
      if (verifyError) throw verifyError;

      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
      setVerifyCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && verifyCode.length === 6) verifyFactor();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--rp-bg)' }}>
      <div className="max-w-md w-full rounded-2xl p-8 shadow-2xl" style={{ backgroundColor: 'var(--rp-surface, #1a1a2e)', border: '1px solid rgba(255,255,255,0.1)' }}>
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold" style={{ color: 'var(--rp-text)' }}>Set Up Two-Factor Authentication</h2>
          <p className="text-sm mt-2" style={{ color: 'var(--rp-text-secondary)' }}>
            Scan the QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.)
          </p>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg text-sm" style={{ backgroundColor: 'rgba(255,59,48,0.15)', color: '#FF3B30', border: '1px solid rgba(255,59,48,0.3)' }}>
            {error}
          </div>
        )}

        {loading && !qrCode ? (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white rounded-full mx-auto mb-4" />
            <p style={{ color: 'var(--rp-text-secondary)' }}>Generating QR code...</p>
          </div>
        ) : step === 'verify' && qrCode ? (
          <>
            {/* QR Code */}
            <div className="flex justify-center mb-4">
              <div className="bg-white p-3 rounded-xl">
                <img src={qrCode} alt="TOTP QR Code" className="w-48 h-48" />
              </div>
            </div>

            {/* Manual secret */}
            <div className="mb-6">
              <button onClick={() => setShowSecret(!showSecret)} className="text-xs underline" style={{ color: 'var(--rp-text-tertiary)' }}>
                {showSecret ? 'Hide' : 'Show'} manual setup key
              </button>
              {showSecret && secret && (
                <div className="mt-2 px-3 py-2 rounded-lg text-xs font-mono break-all select-all" style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'var(--rp-text)', border: '1px solid rgba(255,255,255,0.15)' }}>
                  {secret}
                </div>
              )}
            </div>

            {/* Verification */}
            <div className="space-y-3">
              <label className="text-sm font-medium" style={{ color: 'var(--rp-text)' }}>Enter the 6-digit code from your app</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={handleKeyDown}
                placeholder="000000"
                autoFocus
                className="w-full px-4 py-3 rounded-lg text-center text-2xl font-mono tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'var(--rp-text)', border: '1px solid rgba(255,255,255,0.15)' }}
              />
              <button
                onClick={verifyFactor}
                disabled={loading || verifyCode.length !== 6}
                className="w-full py-3 rounded-lg font-semibold text-sm transition-all hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
                style={{ backgroundColor: '#34C759', color: '#ffffff' }}
              >
                {loading ? 'Verifying...' : 'Verify & Enable 2FA'}
              </button>
            </div>
          </>
        ) : null}

        {onSkip && (
          <button onClick={onSkip} className="w-full mt-3 py-2 text-sm transition-all hover:opacity-70" style={{ color: 'var(--rp-text-tertiary)' }}>
            {required ? 'Back to sign in' : 'Skip for now (not recommended)'}
          </button>
        )}
      </div>
    </div>
  );
};
